import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Claim } from '@wsphere/warranties/domain';
import { firstValueFrom } from 'rxjs';

export interface ClaimItemOwnerOption {
  ownerType: Claim.ClaimItem.Hold.OwnerType;
  ownerId: string;
  name: string;
}

/** Evidence holds and escalations on a repair job. */
@Injectable()
export class ClaimItemHoldService {
  private http = inject(HttpClient);

  private base(claimId: string, itemId: string) {
    return `/api/claims/${claimId}/items/${itemId}`;
  }

  holds(claimId: string, itemId: string) {
    return firstValueFrom(this.http.get<Claim.ClaimItem.Hold.ListHoldsResponse>(`${this.base(claimId, itemId)}/holds`));
  }

  open(claimId: string, itemId: string, body: Claim.ClaimItem.Hold.OpenHoldRequest) {
    return firstValueFrom(
      this.http.post<Claim.ClaimItem.Hold.HoldResponse>(`${this.base(claimId, itemId)}/holds`, body)
    );
  }

  complete(claimId: string, itemId: string, holdId: string, note: string | null, documentIds: string[] = []) {
    const body: Claim.ClaimItem.Hold.CompleteHoldRequest = { note, documentIds };
    return firstValueFrom(
      this.http.patch<Claim.ClaimItem.Hold.HoldResponse>(`${this.base(claimId, itemId)}/holds/${holdId}/complete`, body)
    );
  }

  /** Links claim files to an open hold; ones already linked are skipped. */
  attachEvidence(claimId: string, itemId: string, holdId: string, documentIds: string[]) {
    const body: Claim.ClaimItem.Hold.AttachEvidenceRequest = { documentIds };
    return firstValueFrom(
      this.http.post<Claim.ClaimItem.Hold.HoldResponse>(`${this.base(claimId, itemId)}/holds/${holdId}/evidence`, body)
    );
  }

  cancel(claimId: string, itemId: string, holdId: string, reason: string) {
    return firstValueFrom(
      this.http.patch<Claim.ClaimItem.Hold.HoldResponse>(`${this.base(claimId, itemId)}/holds/${holdId}/cancel`, {
        reason,
      })
    );
  }

  escalations(claimId: string, itemId: string) {
    return firstValueFrom(
      this.http.get<Claim.ClaimItem.Escalation.ListEscalationsResponse>(`${this.base(claimId, itemId)}/escalations`)
    );
  }

  escalate(claimId: string, itemId: string, body: Claim.ClaimItem.Escalation.EscalateRequest) {
    return firstValueFrom(
      this.http.post<Claim.ClaimItem.Escalation.EscalationResponse>(`${this.base(claimId, itemId)}/escalations`, body)
    );
  }

  resolveEscalation(claimId: string, itemId: string, escalationId: string, note: string | null) {
    return firstValueFrom(
      this.http.patch<Claim.ClaimItem.Escalation.EscalationResponse>(
        `${this.base(claimId, itemId)}/escalations/${escalationId}/resolve`,
        { note }
      )
    );
  }

  owners(claimId: string) {
    return firstValueFrom(this.http.get<{ items: ClaimItemOwnerOption[] }>(`/api/claims/${claimId}/item-owners`));
  }
}
